"use client"

import * as React from "react"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis
} from "@workspace/ui/components/pagination"
import { UIButton } from "../buttons/ui-button"
import { UIInputBase as Input } from "../forms/ui-input"
import { UISelectBase as Select, UISelectContentBase as SelectContent, UISelectItemBase as SelectItem, UISelectTriggerBase as SelectTrigger, UISelectValueBase as SelectValue } from "../forms/ui-select"
import { cn } from "@workspace/ui/lib/utils"
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from "lucide-react"
import { UITypography } from "../display/ui-typography"
import { UIBox } from "../layout/ui-box"

// Base Pagination exports - for other composed components
export {
  Pagination as UIPaginationBase,
  PaginationContent as UIPaginationContentBase,
  PaginationItem as UIPaginationItemBase,
  PaginationLink as UIPaginationLinkBase,
  PaginationNext as UIPaginationNextBase,
  PaginationPrevious as UIPaginationPreviousBase,
  PaginationEllipsis as UIPaginationEllipsisBase
} from "@workspace/ui/components/pagination"

// ============================================================================
// UIPagination Types
// ============================================================================

export interface UIPaginationProps {
  currentPage: number
  totalPages: number
  totalItems?: number
  pageSize?: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  className?: string
}

export interface UIPaginationAdvancedProps extends UIPaginationProps {
  showPageSizeSelector?: boolean
  pageSizeOptions?: number[]
  showFirstLast?: boolean
  showInfo?: boolean
  showGoToPage?: boolean
  compact?: boolean
  disabled?: boolean
}

// ============================================================================
// UIPagination Component
// ============================================================================

export const UIPagination = React.forwardRef<
  HTMLElement,
  UIPaginationProps
>(({
  currentPage,
  totalPages,
  totalItems,
  pageSize = 10,
  onPageChange,
  className,
  ...props
}, ref) => {
  const renderPageNumbers = () => {
    const pages = []
    const showEllipsis = totalPages > 7

    if (!showEllipsis) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <PaginationItem key={i}>
            <PaginationLink
              href="#"
              isActive={currentPage === i}
              onClick={(e) => {
                e.preventDefault()
                onPageChange(i)
              }}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        )
      }
    } else {
      // Show ellipsis logic for many pages
      pages.push(
        <PaginationItem key={1}>
          <PaginationLink
            href="#"
            isActive={currentPage === 1}
            onClick={(e) => {
              e.preventDefault()
              onPageChange(1)
            }}
          >
            1
          </PaginationLink>
        </PaginationItem>
      )

      if (currentPage > 3) {
        pages.push(
          <PaginationItem key="ellipsis-start">
            <PaginationEllipsis />
          </PaginationItem>
        )
      }

      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(
          <PaginationItem key={i}>
            <PaginationLink
              href="#"
              isActive={currentPage === i}
              onClick={(e) => {
                e.preventDefault()
                onPageChange(i)
              }}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        )
      }

      if (currentPage < totalPages - 2) {
        pages.push(
          <PaginationItem key="ellipsis-end">
            <PaginationEllipsis />
          </PaginationItem>
        )
      }

      if (totalPages > 1) {
        pages.push(
          <PaginationItem key={totalPages}>
            <PaginationLink
              href="#"
              isActive={currentPage === totalPages}
              onClick={(e) => {
                e.preventDefault()
                onPageChange(totalPages)
              }}
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        )
      }
    }

    return pages
  }

  return (
    <Pagination ref={ref} className={cn(className)} {...props}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(e) => {
              e.preventDefault()
              if (currentPage > 1) onPageChange(currentPage - 1)
            }}
            className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>

        {renderPageNumbers()}

        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(e) => {
              e.preventDefault()
              if (currentPage < totalPages) onPageChange(currentPage + 1)
            }}
            className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
})

UIPagination.displayName = "UIPagination"

// ============================================================================
// UIPaginationAdvanced Component
// ============================================================================

export const UIPaginationAdvanced = React.forwardRef<
  HTMLDivElement,
  UIPaginationAdvancedProps
>(({
  currentPage,
  totalPages,
  totalItems,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  pageSizeOptions = [10, 20, 50, 100],
  showFirstLast = true,
  showInfo = true,
  showGoToPage = true,
  compact = false,
  disabled = false,
  className,
  ...props
}, ref) => {
  const [goToPage, setGoToPage] = React.useState("")

  const handleGoToPage = () => {
    const page = parseInt(goToPage)
    if (page >= 1 && page <= totalPages) {
      onPageChange(page)
      setGoToPage("")
    }
  }

  const startItem = ((currentPage - 1) * pageSize) + 1
  const endItem = Math.min(currentPage * pageSize, totalItems || 0)

  if (compact) {
    return (
      <UIBox ref={ref} className={cn("flex items-center justify-between space-x-2", className)} {...props}>
        <UIBox className="flex items-center space-x-1">
          <UIButton
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={disabled || currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous</span>
          </UIButton>
          <UITypography variant="body2" color="muted" className="px-2">
            {currentPage} / {totalPages}
          </UITypography>
          <UIButton
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={disabled || currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next</span>
          </UIButton>
        </UIBox>

        {showPageSizeSelector && onPageSizeChange && (
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(parseInt(value))}
            disabled={disabled}
          >
            <SelectTrigger className="w-auto h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </UIBox>
    )
  }

  return (
    <UIBox ref={ref} className={cn("space-y-4", className)} {...props}>
      {/* Info and Page Size Selector */}
      <UIBox className="flex items-center justify-between">
        <UIBox className="flex items-center space-x-4">
          {showInfo && totalItems && (
            <UITypography variant="body2" color="muted">
              Showing {startItem.toLocaleString()} to {endItem.toLocaleString()} of {totalItems.toLocaleString()} items
            </UITypography>
          )}
        </UIBox>

        <UIBox className="flex items-center space-x-4">
          {showPageSizeSelector && onPageSizeChange && (
            <UIBox className="flex items-center space-x-2">
              <UITypography as="span" variant="body2" color="muted">Items per page:</UITypography>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => onPageSizeChange(parseInt(value))}
                disabled={disabled}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </UIBox>
          )}
        </UIBox>
      </UIBox>

      {/* Pagination Controls */}
      <UIBox className="flex items-center justify-between">
        <UIBox className="flex items-center space-x-2">
          {showFirstLast && (
            <UIButton
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={disabled || currentPage === 1}
            >
              <ChevronFirst className="h-4 w-4" />
              <UITypography as="span" variant="small" className="sr-only">First page</UITypography>
            </UIButton>
          )}
        </UIBox>

        {/* Main Pagination */}
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (currentPage > 1) onPageChange(currentPage - 1)
                }}
                className={disabled || currentPage === 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>

            {/* Page Numbers */}
            {(() => {
              const pages = []
              const showEllipsis = totalPages > 7

              if (!showEllipsis) {
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <PaginationItem key={i}>
                      <PaginationLink
                        href="#"
                        isActive={currentPage === i}
                        onClick={(e) => {
                          e.preventDefault()
                          if (!disabled) onPageChange(i)
                        }}
                        className={disabled ? "pointer-events-none opacity-50" : ""}
                      >
                        {i}
                      </PaginationLink>
                    </PaginationItem>
                  )
                }
              } else {
                // First page
                pages.push(
                  <PaginationItem key={1}>
                    <PaginationLink
                      href="#"
                      isActive={currentPage === 1}
                      onClick={(e) => {
                        e.preventDefault()
                        if (!disabled) onPageChange(1)
                      }}
                      className={disabled ? "pointer-events-none opacity-50" : ""}
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>
                )

                if (currentPage > 3) {
                  pages.push(
                    <PaginationItem key="ellipsis-start">
                      <PaginationEllipsis />
                    </PaginationItem>
                  )
                }

                const start = Math.max(2, currentPage - 1)
                const end = Math.min(totalPages - 1, currentPage + 1)

                for (let i = start; i <= end; i++) {
                  pages.push(
                    <PaginationItem key={i}>
                      <PaginationLink
                        href="#"
                        isActive={currentPage === i}
                        onClick={(e) => {
                          e.preventDefault()
                          if (!disabled) onPageChange(i)
                        }}
                        className={disabled ? "pointer-events-none opacity-50" : ""}
                      >
                        {i}
                      </PaginationLink>
                    </PaginationItem>
                  )
                }

                if (currentPage < totalPages - 2) {
                  pages.push(
                    <PaginationItem key="ellipsis-end">
                      <PaginationEllipsis />
                    </PaginationItem>
                  )
                }

                // Last page
                if (totalPages > 1) {
                  pages.push(
                    <PaginationItem key={totalPages}>
                      <PaginationLink
                        href="#"
                        isActive={currentPage === totalPages}
                        onClick={(e) => {
                          e.preventDefault()
                          if (!disabled) onPageChange(totalPages)
                        }}
                        className={disabled ? "pointer-events-none opacity-50" : ""}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )
                }
              }

              return pages
            })()}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (currentPage < totalPages) onPageChange(currentPage + 1)
                }}
                className={disabled || currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>

        <UIBox className="flex items-center space-x-2">
          {showFirstLast && (
            <UIButton
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
              disabled={disabled || currentPage === totalPages}
            >
              <ChevronLast className="h-4 w-4" />
              <UITypography as="span" variant="small" className="sr-only">Last page</UITypography>
            </UIButton>
          )}
        </UIBox>
      </UIBox>

      {/* Go to Page */}
      {showGoToPage && (
        <UIBox className="flex items-center justify-center space-x-2">
          <UITypography as="span" variant="body2" color="muted">Go to page:</UITypography>
          <Input
            type="number"
            min={1}
            max={totalPages}
            value={goToPage}
            onChange={(e) => setGoToPage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleGoToPage()
              }
            }}
            className="w-20"
            placeholder="Page"
            disabled={disabled}
          />
          <UIButton
            variant="outline"
            size="sm"
            onClick={handleGoToPage}
            disabled={disabled || !goToPage}
          >
            Go
          </UIButton>
        </UIBox>
      )}
    </UIBox>
  )
})

UIPaginationAdvanced.displayName = "UIPaginationAdvanced"

// ============================================================================
// Preset Components
// ============================================================================

export interface UIPaginationSimpleProps extends UIPaginationProps {
  showInfo?: boolean
}

export const UIPaginationSimple = React.forwardRef<
  HTMLDivElement,
  UIPaginationSimpleProps
>(({
  currentPage,
  totalPages,
  totalItems,
  pageSize = 10,
  showInfo = true,
  className,
  ...props
}, ref) => {
  const startItem = ((currentPage - 1) * pageSize) + 1
  const endItem = Math.min(currentPage * pageSize, totalItems || 0)

  return (
    <UIBox ref={ref} className={cn("space-y-4", className)}>
      {showInfo && totalItems && (
        <UITypography variant="body2" color="muted" align="center">
          Page {currentPage} of {totalPages} ({totalItems.toLocaleString()} total items)
        </UITypography>
      )}

      <UIPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        {...props}
      />
    </UIBox>
  )
})

UIPaginationSimple.displayName = "UIPaginationSimple"

export interface UIPaginationTableProps extends UIPaginationAdvancedProps {
  // Specialized for data tables
}

export const UIPaginationTable = React.forwardRef<
  HTMLDivElement,
  UIPaginationTableProps
>(({ className, ...props }, ref) => {
  return (
    <UIPaginationAdvanced
      ref={ref}
      showPageSizeSelector={true}
      showFirstLast={true}
      showInfo={true}
      showGoToPage={false}
      compact={false}
      className={cn("border-t pt-4", className)}
      {...props}
    />
  )
})

UIPaginationTable.displayName = "UIPaginationTable"

export interface UIPaginationMobileProps extends UIPaginationAdvancedProps {
  // Optimized for mobile
}

export const UIPaginationMobile = React.forwardRef<
  HTMLDivElement,
  UIPaginationMobileProps
>(({ className, ...props }, ref) => {
  return (
    <UIPaginationAdvanced
      ref={ref}
      compact={true}
      showPageSizeSelector={true}
      showFirstLast={false}
      showInfo={false}
      showGoToPage={false}
      className={cn(className)}
      {...props}
    />
  )
})

UIPaginationMobile.displayName = "UIPaginationMobile"

// ============================================================================
// Type Re-exports
// ============================================================================

